import React, { useState, useEffect } from 'react';
import { API, graphqlOperation, Auth, Storage } from 'aws-amplify';
import { getCourse, courseModulesByCourseID, getModule, listVideoURLs } from '../graphql/queries';
import JourneyModal from './JourneyModal';
import { getCourseConfig } from '../config/courses';

const CourseModalManager = ({ 
  activeCourseKey, 
  isOpen, 
  onRequestClose, 
  favorites, 
  onFavoriteToggle, 
  instructors 
}) => {
  const [courseData, setCourseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoURLsData, setVideoURLsData] = useState([]);
  const [isLoadingVideoURLs, setIsLoadingVideoURLs] = useState(false);

  const courseConfig = getCourseConfig(activeCourseKey);

  // Fetch all video URLs from the VideoURLs table
  useEffect(() => {
    const fetchAllVideoURLs = async () => {
      if (!isOpen) return;
      
      setIsLoadingVideoURLs(true);
      let allItems = [];
      let nextToken = null;
      
      try {
        do {
          const videoUrlResponse = await API.graphql(
            graphqlOperation(listVideoURLs, { 
              limit: 10000,
              nextToken: nextToken
            })
          );
          
          const items = videoUrlResponse.data.listVideoURLs.items;
          nextToken = videoUrlResponse.data.listVideoURLs.nextToken;
          allItems = [...allItems, ...items];

        } while (nextToken);

        // Cache the video URLs data
        setVideoURLsData(allItems);
      } catch (error) {
        console.error("CourseModalManager - Error fetching video URLs:", error);
      } finally {
        setIsLoadingVideoURLs(false);
      }
    };

    // Only fetch if we don't already have the data
    if (isOpen && (!videoURLsData || videoURLsData.length === 0)) {
      fetchAllVideoURLs();
    }
  }, [isOpen]); // Only depend on isOpen

  useEffect(() => {
    if (!isOpen || !activeCourseKey || !courseConfig) {
      setCourseData(null);
      return;
    }

    const fetchCourseData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const user = await Auth.currentAuthenticatedUser();
        const userSub = user.attributes.sub;
        
        const courseResponse = await API.graphql(
          graphqlOperation(getCourse, { id: courseConfig.id })
        );
        const course = courseResponse.data.getCourse;
        
        // Get CourseModules for this course
        const courseModulesResponse = await API.graphql(
          graphqlOperation(courseModulesByCourseID, {
            courseID: courseConfig.id,
            filter: { _deleted: { ne: true } }
          })
        );
        
        const courseModules = courseModulesResponse.data.courseModulesByCourseID.items;

        // Get full module data
        const modulesData = await Promise.all(
          courseModules.map(async (courseModule) => {
            const moduleResponse = await API.graphql(
              graphqlOperation(getModule, { id: courseModule.moduleID })
            );
            const moduleData = moduleResponse.data.getModule;
            
            return {
              ...moduleData,
              order: courseModule.order
            };
          })
        );

        course.modules = modulesData
          .filter(module => module !== null)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        if (course.image) {
          const imageUrl = await Storage.get(course.image);
          course.imageUrl = imageUrl;
        }

        setCourseData(course);
      } catch (error) {
        console.error("Error fetching course data:", error);
        setError("Failed to load course data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseData();
  }, [isOpen, activeCourseKey, courseConfig]);

  if (!courseConfig) {
    return null;
  }

  return (
    <JourneyModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      courseData={courseData}
      courseConfig={courseConfig}
      favorites={favorites}
      onFavoriteToggle={onFavoriteToggle}
      instructors={instructors}
      videoURLsData={videoURLsData}
    />
  );
};

export default CourseModalManager; 